// La landing di poptown.it (tela «PopTown Pop Style», artboard «Landing page»). Si monta in
// main.tsx fuori dalla plancia, quando il server la segnala per l'host (POPTOWN_LANDING_HOSTS):
// niente store, stream né chiamate all'API. Nessun link porta all'applicazione, e il suo
// indirizzo non compare nella pagina: lo conosce solo l'utente (2026-11-01, «niente deve andare
// in app», «app è nascosto, lo so solo io»).
import { LandingScene } from './LandingScene';
import { POP_ROLES } from '../../lib/pops';
import { CREW, DEEP, FAQ, FEATURES, STEPS, pop, type Tint } from './landingData';
import './landing.css';

// gli altri mestieri dell'app (lib/pops.ts), oltre a quelli delle card: chi visita deve sapere
// che la squadra è più grande e che si allarga (richiesta dell'utente del 2026-11-01)
const MORE = POP_ROLES.filter((r) => !CREW.some((c) => c.avatar === r.id));
const TINTS: Tint[] = ['blue', 'lilac', 'pink', 'coral', 'mint', 'sun'];

const STAR = 'M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z';

function Glyph({ d, size = 24 }: { d: string; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

export function Landing() {
  document.title = 'PopTown · agenti AI che lavorano in squadra';
  return (
    <div className="ld">
      <header className="ld-top">
        <img src="/poptown-logo.webp" alt="PopTown" className="ld-logo" />
        <nav aria-label="Sezioni" className="ld-nav">
          <a href="#come">Come funziona</a>
          <a href="#pop">I pop</a>
          <a href="#cosa">Cosa sanno fare</a>
        </nav>
        <a href="#sala" className="ld-btn ld-btn-primary">
          Guarda i pop al lavoro
        </a>
      </header>

      <section className="ld-hero">
        <svg aria-hidden="true" className="ld-star ld-star-a" viewBox="0 0 24 24">
          <path fill="currentColor" d={STAR} />
        </svg>
        <svg aria-hidden="true" className="ld-star ld-star-b" viewBox="0 0 24 24">
          <path fill="currentColor" d={STAR} />
        </svg>
        <div className="ld-hero-text">
          <span className="ld-chip">
            <span className="ld-pulse" />
            Una squadra di agenti che lavora mentre dormi
          </span>
          <h1 className="ld-h1">
            La città dove le idee <span>si fanno da sole</span>.
          </h1>
          <p className="ld-lead">
            Racconti un’idea al PM. I pop la discutono, si dividono il lavoro, usano i tuoi
            strumenti e ti portano il risultato. Tu approvi.
          </p>
          <div className="ld-actions">
            <a href="#cosa" className="ld-btn ld-btn-primary ld-btn-lg">
              Scopri cosa sanno fare
              <Glyph d="M5 12h14 M13 6l6 6-6 6" size={20} />
            </a>
          </div>
        </div>
        <LandingScene />
      </section>

      <section id="pop" className="ld-band">
        <div className="ld-wrap ld-center">
          <h2 className="ld-h2">Conosci la squadra</h2>
          <p className="ld-sub">
            Ogni pop ha un mestiere, un nome e il suo modello. Il PM sceglie chi chiamare per ogni
            idea.
          </p>
          <div className="ld-crew">
            {CREW.map((p, i) => (
              <div key={p.name} className="ld-card ld-member" data-tint={p.tint}>
                <div className="ld-portrait">
                  <img
                    src={pop(p.avatar)}
                    alt={`${p.name}, ${p.role}`}
                    style={{ animationDelay: `${i * 0.25}s` }}
                  />
                </div>
                <div className="ld-member-name">{p.name}</div>
                <div className="ld-tag">{p.role}</div>
              </div>
            ))}
          </div>
          <div className="ld-more">
            <p className="ld-more-title">
              E non sono i soli: in città vivono altri {MORE.length} mestieri.
            </p>
            <ul className="ld-more-list">
              {MORE.map((r, i) => (
                <li key={r.id} className="ld-chip" data-tint={TINTS[i % TINTS.length]}>
                  <img src={pop(r.id)} alt="" />
                  {r.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="ld-card ld-create">
            <div className="ld-create-plus" aria-hidden="true">
              <Glyph d="M12 5v14M5 12h14" size={34} />
            </div>
            <div>
              <h3 className="ld-create-title">Ti serve un pop che non c’è? Crealo su misura.</h3>
              <p className="ld-create-text">
                Dai un nome, un mestiere e le istruzioni, scegli il modello e l’avatar: il nuovo pop
                entra in squadra e il PM può chiamarlo da subito. Puoi averne quanti ne vuoi, anche
                più di uno con lo stesso ruolo.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="come" className="ld-wrap ld-section">
        <h2 className="ld-h2 ld-center">Tre passi, e la città lavora</h2>
        <div className="ld-grid ld-grid-3">
          {STEPS.map((s, i) => (
            <article key={s.title} className="ld-card ld-step" data-tint={s.tint}>
              <span className="ld-num">{i + 1}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="cosa" className="ld-wrap ld-section ld-tight">
        <h2 className="ld-h2 ld-center">Cosa sanno fare</h2>
        <div className="ld-grid ld-grid-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="ld-feature" data-tint={f.tint}>
              <span className="ld-feature-icon">
                <Glyph d={f.d} />
              </span>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ld-wrap ld-narrow ld-section ld-tight">
        <div className="ld-card ld-about">
          <h2 className="ld-h2">Cos’è PopTown</h2>
          <p>
            PopTown è una piattaforma di <strong>agenti AI che lavorano in squadra</strong>, da
            installare sul proprio server. Un project manager virtuale ascolta la richiesta, la
            trasforma in un piano e la affida a un team di agenti specializzati: ricerca, sviluppo,
            sicurezza, analisi dei dati, scrittura, posta. Ogni agente usa il modello più adatto
            (Claude, Codex, Gemini o un modello locale) e solo gli strumenti che gli concedi.
          </p>
          <p>
            È pensato per chi vuole{' '}
            <strong>automatizzare il lavoro ripetitivo con l’intelligenza artificiale</strong> senza
            perdere il controllo: ogni passo è visibile dal vivo, le azioni che scrivono si
            accendono una per una, e il risultato arriva in chat, per email o come notifica.
          </p>
        </div>
        <div className="ld-grid ld-grid-2">
          {DEEP.map((d) => (
            <article key={d.title} className="ld-card ld-deep" data-tint={d.tint}>
              <div className="ld-deep-head">
                <span className="ld-feature-icon ld-tinted">
                  <Glyph d={d.d} size={22} />
                </span>
                <h3>{d.title}</h3>
              </div>
              <p>{d.text}</p>
              <ul>
                {d.points.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <div className="ld-faq">
          <h2 className="ld-h2">Domande frequenti</h2>
          {FAQ.map((q) => (
            <details key={q.q} className="ld-card">
              <summary>
                <Glyph d="M9 6l6 6-6 6" size={18} />
                {q.q}
              </summary>
              <p>{q.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section id="entra" className="ld-final">
        <img src="/town/poptown-town.webp" alt="" />
        <div className="ld-final-veil" />
        <div className="ld-final-text">
          <h2>Stanotte la città è sveglia. Tu dormi.</h2>
          <p>Domattina trovi il lavoro fatto e un pulsante per approvarlo.</p>
          <a href="#cosa" className="ld-btn ld-btn-sun ld-btn-lg">
            Scopri cosa sanno fare
          </a>
        </div>
      </section>

      <footer className="ld-wrap ld-foot">
        <a href="/privacy">Privacy</a>
        <a href="/termini-di-utilizzo">Termini di utilizzo</a>
      </footer>
    </div>
  );
}
